import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';

import { normalizeStatusText } from '../types/contractStatus';

/**
 * Radical de "cancelada"/"cancelado" — casa com as duas flexões e com o texto
 * acentuado depois da normalização.
 */
const CANCELLED_KEYWORD = 'cancelad';

/**
 * A parcela está cancelada?
 *
 * `IndividualPaymentParcelViewModel` traz `status`/`statusId` como números sem
 * enum gerado, então o único sinal legível é o `statusText`. O casamento é por
 * substring normalizada, a mesma estratégia de `getContractStatusTone` — que
 * não pode ser reusado aqui porque o tom `negative` junta `cancelad`, `vencid`,
 * `atrasad` e `estornad` num só balde.
 *
 * @param parcel - Parcela do contrato financeiro.
 * @returns `true` quando o status textual indica cancelamento.
 *
 * @example
 * ```ts
 * isParcelCancelled({ statusText: 'Cancelada' }); // true
 * isParcelCancelled({ statusText: 'Vencida' });   // false
 * ```
 */
export function isParcelCancelled(parcel: IndividualPaymentParcelViewModel): boolean {
  if (!parcel.statusText) return false;
  return normalizeStatusText(parcel.statusText).includes(CANCELLED_KEYWORD);
}

/**
 * A parcela está paga?
 *
 * Decidido por dado objetivo, não por texto: uma parcela é paga quando tem data
 * de pagamento ou valor pago maior que zero.
 *
 * @param parcel - Parcela do contrato financeiro.
 * @returns `true` quando há baixa registrada.
 *
 * @example
 * ```ts
 * isParcelPaid({ paymentDate: '2026-02-08T00:00:00Z' }); // true
 * isParcelPaid({ totalPaid: 0 });                        // false
 * ```
 */
export function isParcelPaid(parcel: IndividualPaymentParcelViewModel): boolean {
  if (parcel.paymentDate) return true;
  return (parcel.totalPaid ?? 0) > 0;
}

/**
 * A parcela está em aberto?
 *
 * Em aberto é o complemento: sem baixa e não cancelada. Vencida e atrasada
 * continuam em aberto — é isso que as torna elegíveis ao cancelamento em lote.
 *
 * Um `statusText` novo e desconhecido não casa com o radical de cancelamento,
 * então cai aqui se não tiver pagamento. É o lado seguro: entra numa ação
 * reversível em vez de desaparecer da tela.
 *
 * @param parcel - Parcela do contrato financeiro.
 * @returns `true` quando a parcela ainda pode ser cobrada.
 *
 * @example
 * ```ts
 * isParcelOpen({ statusText: 'Em aberto' }); // true
 * isParcelOpen({ statusText: 'Cancelada' }); // false
 * ```
 */
export function isParcelOpen(parcel: IndividualPaymentParcelViewModel): boolean {
  return !isParcelPaid(parcel) && !isParcelCancelled(parcel);
}
